<div class="p-4 mt-2 bg-white rounded-2xl border">
  <div class="flex items-center justify-between">
    <empro-typography type="large" weight="medium">
      Datenexport
    </empro-typography>
    <div class="flex items-center gap-4">
      <div class="flex items-center">
        <input
          id="tenant-export-include-files"
          data-cy="tenant-export-include-files"
          type="checkbox"
          class="w-5 h-5 text-cyan-600 border-gray-300 rounded focus:ring-0 self-center"
          [checked]="includeFiles"
          [disabled]="isStarting"
          (change)="onIncludeFilesChange($event)"
        />
        <label for="tenant-export-include-files" class="pl-2 text-sm text-black">
          Dateien exportieren
        </label>
      </div>
      <empro-button
        [isLoading]="isStarting"
        [disabled]="hasRunningExport || isStarting"
        fill="outline"
        size="small"
        (onClick)="startExport()"
      >
        <empro-typography type="small" weight="bold">
          Export starten
        </empro-typography>
      </empro-button>
    </div>
  </div>

  <empro-alert-box
    *ngIf="errorMessage"
    class="w-full mt-2 block"
    [header]="'Error'"
    [message]="errorMessage"
    [variant]="'error'"
  />

  <div class="relative mt-2">
    <empro-spinner [show]="isLoading" />

    @if (!isLoading && exports.length === 0) {
      <p class="text-sm text-gray-500">Noch keine Exporte vorhanden.</p>
    }

    @for (exportJob of visibleExports; track exportJob.exportId) {
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 py-2 border-b last:border-b-0 items-center"
        [attr.data-cy]="'tenant-export-' + exportJob.exportId"
      >
        <div class="relative">
          <p class="text-sm text-gray-500">Gestartet</p>
          <p class="text-sm break-words">
            {{ exportJob.startedDate | date: 'dd.MM.yyyy HH:mm' }}
          </p>
        </div>
        <div class="relative">
          <p class="text-sm text-gray-500">Von</p>
          <p class="text-sm break-words">{{ exportJob.requestedBy }}</p>
        </div>
        <div class="relative">
          <p class="text-sm text-gray-500">Status</p>
          <div class="w-24">
            <empro-shared-badge
              [variant]="badgeVariant(exportJob)"
              [hover]="false"
              [hasBorder]="false"
              [rounded]="16"
              size="small"
              shadow="small"
            >
              {{ exportJob.status }}
            </empro-shared-badge>
          </div>
        </div>
        <div class="relative">
          <p class="text-sm text-gray-500">
            {{ exportJob.status === 'RUNNING' ? 'Fortschritt' : 'Größe' }}
          </p>
          <p class="text-sm break-words">
            @if (exportJob.status === 'RUNNING') {
              {{ exportJob.progress?.phase }}
            } @else if (exportJob.status === 'FAILED') {
              {{ exportJob.error }}
            } @else {
              {{ formatSize(exportJob.summary?.totalSizeBytes) }}
            }
          </p>
        </div>
        <div class="relative flex justify-end">
          @if (exportJob.status === 'RUNNING') {
            <empro-button
              fill="outline"
              size="small"
              [isLoading]="isCancelling"
              [disabled]="isCancelling"
              [attr.data-cy]="'tenant-export-cancel-' + exportJob.exportId"
              (onClick)="cancelExport(exportJob)"
            >
              <empro-typography type="small" weight="bold">
                Abbrechen
              </empro-typography>
            </empro-button>
          }
          @if (exportJob.status === 'SUCCEEDED') {
            <empro-button
              fill="outline"
              size="small"
              [attr.data-cy]="'tenant-export-files-' + exportJob.exportId"
              (onClick)="openFilesModal(exportJob)"
            >
              <empro-typography type="small" weight="bold">
                Downloads ({{ exportJob.summary?.archiveFiles?.length ?? 0 }})
              </empro-typography>
            </empro-button>
          }
        </div>
      </div>
    }

    @if (hiddenExportCount > 0) {
      <div class="flex justify-center pt-2">
        <empro-button
          fill="clear"
          size="small"
          data-cy="tenant-export-toggle-history"
          (onClick)="showAllExports = !showAllExports"
        >
          <empro-typography type="small" weight="bold">
            {{
              showAllExports
                ? 'Nur letzten Export anzeigen'
                : hiddenExportCount + ' ältere Exporte anzeigen'
            }}
          </empro-typography>
        </empro-button>
      </div>
    }
  </div>
</div>

<empro-modal
  [open]="fileModalExport !== undefined"
  [hasClose]="true"
  [closeOutside]="true"
  (onClose)="closeFilesModal()"
  sizeY="large"
>
  <div class="content" content>
    <div modalTitle>
      <empro-typography class="title" type="large" weight="medium">
        Export-Downloads
      </empro-typography>
    </div>

    <div modalContent class="text-left">
      @if (fileModalExport) {
        <p class="text-sm text-gray-500 mb-2">
          Export vom
          {{ fileModalExport.startedDate | date: 'dd.MM.yyyy HH:mm' }} ·
          {{ fileModalExport.summary?.archiveFiles?.length ?? 0 }} Dateien ·
          {{ formatSize(fileModalExport.summary?.totalSizeBytes) }} gesamt
        </p>
        <div class="max-h-96 overflow-y-auto divide-y">
          @for (
            file of fileModalExport.summary?.archiveFiles ?? [];
            track file.name
          ) {
            <div
              class="flex items-center justify-between py-1"
              [attr.data-cy]="'tenant-export-file-' + file.name"
            >
              <p class="text-sm break-all">{{ file.name }}</p>
              <div class="flex items-center gap-x-4 shrink-0">
                <p class="text-sm text-gray-500 w-20 text-right">
                  {{ formatSize(file.sizeBytes) }}
                </p>
                <empro-button
                  fill="clear"
                  size="small"
                  [isLoading]="downloadingFile === file.name"
                  [disabled]="downloadingFile !== undefined"
                  [attr.data-cy]="'tenant-export-download-' + file.name"
                  (onClick)="downloadFile(fileModalExport, file.name)"
                >
                  <empro-typography type="small" weight="bold">
                    Download
                  </empro-typography>
                </empro-button>
              </div>
            </div>
          }
        </div>
      }
    </div>
  </div>
</empro-modal>
